import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiAlertCircle, FiArrowRight, FiTrendingDown } from 'react-icons/fi';

const ProblemSection = () => {
  const problems = [
    "Your 3rd grader still reads like a kindergartner while friends devour chapter books",
    "Homework takes 4 hours when it should take 30 minutes—and starts with excuses and ends in tears",
    "They can tell you everything about their interests but \"can't think of anything to write\"",
    "You've hired tutors, tried apps, bought workbooks—nothing sticks",
    "Teachers keep saying \"they're not applying themselves\" but you watch them try so hard",
    "Math facts won't stick no matter how many times you practice",
    "They're starting to say \"I'm dumb\" and your heart breaks every time",
    "You suspect they're getting bullied at school",
    "Other kids finish tests while yours is still reading question one",
    "Bedtime becomes a guilt fest: \"Did I push too hard? Should I have helped more?\""
  ];

  const stats = [
    { number: "1 in 5", description: "children experience learning as daily frustration" },
    { number: "1 in 59", description: "children diagnosed with learning disability" },
    { number: "20%", description: "of population affected by dyslexia" },
    { number: "3-4%", description: "of children affected by NVLD" },
    { number: "7-15%", description: "struggle with dysgraphia" },
    { number: "31%", description: "more likely to be bullied" }
  ];

  const cycleSteps = [
    "Weak executive function",
    "Can't access intelligence",
    "Academic failures",
    "\"I'm stupid\" beliefs",
    "Lifelong learning anxiety"
  ];

  return (
    <section id="problems" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Main Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            The Exhausting Reality of Learning on Hard Mode
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            For nearly 1 in 5 children, school isn't just challenging—it's a daily exercise in frustration and failure<sup>6</sup>. If this sounds familiar, you're not alone.
          </p>
        </motion.div>

        {/* Sound Familiar Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="bg-red-50 border-l-4 border-red-400 p-8 rounded-r-xl">
            <h3 className="text-2xl font-bold text-red-800 mb-6 flex items-center gap-3">
              <SafeIcon icon={FiAlertCircle} className="h-8 w-8" />
              Sound Familiar?
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {problems.map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3 text-gray-700"
                >
                  <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>{problem}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* The Vicious Cycle */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-4">
              The Learning Struggles Reality:
            </h3>
          </div>
          
          <div className="flex flex-wrap justify-center items-center gap-4 mb-8">
            {cycleSteps.map((step, index) => (
              <React.Fragment key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-gradient-to-r from-red-100 to-red-200 px-6 py-3 rounded-full text-red-800 font-semibold text-center min-w-48"
                >
                  {step}
                </motion.div>
                {index < cycleSteps.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: index * 0.2 + 0.1 }}
                  >
                    <SafeIcon icon={FiArrowRight} className="h-6 w-6 text-red-500" />
                  </motion.div>
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        {/* Key Facts */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-8 text-center">
            Key Facts Every Parent Should Know:
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card text-center"
              >
                <div className="text-3xl font-bold text-accent-magenta mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.description}</div>
              </motion.div>
            ))}
          </div>

          <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>• Children with learning differences often have above-average intelligence trapped behind processing challenges</p>
            <p>• Executive function weaknesses are the hidden culprit behind most academic struggles</p>
            <p className="font-semibold text-gray-900 bg-yellow-50 p-4 rounded-lg border-l-4 border-accent-yellow">
              These challenges are symptoms of a deeper issue. Research shows that children with learning disorders like dyslexia and dysgraphia consistently show deficits in core executive functions like working memory, cognitive flexibility, and processing speed<sup>5,10</sup>. This is the root cause we address.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSection;